import React, { ReactNode } from 'react';
import { FormCheck, OverlayTrigger, Tooltip } from 'react-bootstrap';
import createCalculator from 'final-form-calculate';
import {
  HeaderDisplayKeysWithType,
  OverflowWithTooltip,
  DateTimeField,
  NumberField,
  CheckBoxField,
  TextField,
  DropdownCheckListField,
  ExaminationReasonField,
  getExaminationReasonLabel,
} from '../../components';
import { PHISight, Sight, SimpleStudent } from '../../model';
import { Nullable } from '../../types';
import { DateTime } from 'luxon';
import { I18N } from '../../i18n-raw';

export const SIGHT_MANAGE_MAP = 'SightManageMap';

let cachedSightManageMap = I18N.SightManageCodeMap;

export const SightContext = {
  [SIGHT_MANAGE_MAP]: cachedSightManageMap,
} as const;

export const SightPropertyFields = {
  sight0L: (
    <NumberField
      property="sight0L"
      min={-10}
      max={20}
      validate={(v) => {
        return v !== undefined && v !== null
          ? (v <= 20 && v >= 1) ||
            [-1, -5, -6, -7, -8, -9].some((i) => i === +v)
            ? undefined
            : `錯誤`
          : undefined;
      }}
    />
  ),
  sight0R: (
    <NumberField
      property="sight0R"
      min={-10}
      max={20}
      validate={(v) =>
        v !== undefined && v !== null
          ? (v <= 20 && v >= 1) ||
            [-1, -5, -6, -7, -8, -9].some((i) => i === +v)
            ? undefined
            : `錯誤`
          : undefined
      }
    />
  ),
  sightR: (
    <NumberField
      property="sightR"
      min={-1}
      max={20}
      validate={(v) =>
        v !== undefined && v !== null
          ? (v <= 20 && v >= 1) || +v === -1
            ? undefined
            : `錯誤`
          : undefined
      }
    />
  ),
  sightL: (
    <NumberField
      property="sightL"
      min={-1}
      max={20}
      validate={(v) =>
        v !== undefined && v !== null
          ? (v <= 20 && v >= 1) || +v === -1
            ? undefined
            : `錯誤`
          : undefined
      }
    />
  ),
  eNearR: (
    <NumberField
      property="eNearR"
      min={1}
      max={5000}
      validate={(v) =>
        v !== undefined && v !== null
          ? v > 5000 || v < 1
            ? `錯誤`
            : undefined
          : undefined
      }
    />
  ),
  eNearL: (
    <NumberField
      property="eNearL"
      min={1}
      max={5000}
      validate={(v) =>
        v !== undefined && v !== null
          ? v > 5000 || v < 1
            ? `錯誤`
            : undefined
          : undefined
      }
    />
  ),
  eFarR: (
    <NumberField
      property="eFarR"
      min={1}
      max={2500}
      validate={(v) =>
        v !== undefined && v !== null
          ? v > 2500 || v < 1
            ? `錯誤`
            : undefined
          : undefined
      }
    />
  ),
  eFarL: (
    <NumberField
      property="eFarL"
      min={1}
      max={2500}
      validate={(v) =>
        v !== undefined && v !== null
          ? v > 2500 || v < 1
            ? `錯誤`
            : undefined
          : undefined
      }
    />
  ),
  eSanR: (
    <NumberField
      property="eSanR"
      min={-2500}
      max={2500}
      validate={(v) =>
        v !== undefined && v !== null
          ? Math.abs(v) > 2500 || Math.abs(v) < 1
            ? `錯誤`
            : undefined
          : undefined
      }
    />
  ),
  eSanL: (
    <NumberField
      property="eSanL"
      min={-2500}
      max={2500}
      validate={(v) =>
        v !== undefined && v !== null
          ? Math.abs(v) > 2500 || Math.abs(v) < 1
            ? `錯誤`
            : undefined
          : undefined
      }
    />
  ),
  manageID: (content: Record<string, string>) => {
    const compareManageId = (a: string, b: string) => {
      const codeA = a.trim();
      const codeB = b.trim();

      const numA = Number(codeA.match(/^\d+/)?.[0] ?? 999);
      const numB = Number(codeB.match(/^\d+/)?.[0] ?? 999);

      if (numA !== numB) return numA - numB;

      return codeA.localeCompare(codeB, undefined, {
        sensitivity: 'base',
      });
    };

    const sortManageId = (values: string[]) =>
      [...values].sort(compareManageId);

    const codeToWording = Object.entries(content)
      .map(([k]) => ({
        value: k,
        label: `${I18N.SightManageCodeMap[k] || '未知處置'}`,
      }))
      .reduce((acc, { value, label }) => {
        acc[value] = label;
        return acc;
      }, {} as Record<string, string>);

    return (
      <DropdownCheckListField
        content={codeToWording}
        property="manageID"
        serialize={(v) => v.join(',')}
        deserialize={(v) =>
          v.replaceAll('，', ',').replaceAll('.', ',').replaceAll('、', ',').split(',')
        }
        sort={sortManageId}
      />
    );
  },
} as const;

export function updateSightManageMap(mapData: Record<string, string>) {
  cachedSightManageMap = mapData;
}
type EyeSightPageData = SimpleStudent & Nullable<PHISight>;
const transformCheckedBox = (
  property: string,
  v: unknown,
  i: number,
  e?: boolean
) =>
  e ? (
    <CheckBoxField property={property} />
  ) : (
    <FormCheck checked={!!v} readOnly type="checkbox" />
  );

const transformTextInput = (
  node: ReactNode,
  v: unknown,
  i: number,
  e?: boolean
) => {
  if (e) {
    return node;
  }
  if (typeof v === 'number') {
    // return v % 1 === 0 ? v.toFixed(1) : v.toString();
    return v % 1 === 0 ? v.toString() : v.toString();
  }
  return v as string;
};

const manageIDMap: Record<string, string> = {
  '1': '長效散瞳劑\n(阿托平 Atropine)',
  '2': '短效散瞳劑',
  '3': '其他藥物',
  '4': '配鏡矯治',
  '5': '更換鏡片',
  '6': '遮眼治療',
  '7a': '配戴隱形眼鏡(軟式)',
  '7b': '配戴隱形眼鏡(硬式)',
  '7c': '配戴隱形眼鏡(軟、硬式)',
  '8': '角膜塑型片',
  '9': '視力保健衛教',
  '10': '其他',
  '11': '定期檢查',
};

export const SightTableHeader: HeaderDisplayKeysWithType<Sight>[] = [
  {
    property: 'isDilated',
    display: '散瞳治療',
    onRender: transformCheckedBox.bind(null, 'isDilated'),
    style: { backgroundColor: '#cacaff' },
    order: 9,
  },
  {
    property: 'sight0R',
    display: '裸視右',
    style: { backgroundColor: '#cacaff' },
    order: 10,
    onRender: (value, i, e) => {
      if (e) return SightPropertyFields.sight0R;
      if (typeof value === 'number') {
        if (value === -1) return '<0.1';
        if (value === -5) return '<0.1\n(全盲)';
        if (value < 0) return value;
        if (value > 0 && value % 1 !== 0) {
          return value.toFixed(1);
        }
        return value.toFixed(1).toString();
      }
      return value as string;
    },
  },
  {
    property: 'sight0L',
    display: '裸視左',
    style: { backgroundColor: '#cacaff' },
    order: 11,
    onRender: (value, i, e) => {
      if (e) return SightPropertyFields.sight0L;
      if (typeof value === 'number') {
        if (value === -1) return '<0.1';
        if (value === -5) return '<0.1\n(全盲)';
        if (value < 0) return value;
        if (value > 0 && value % 1 !== 0) {
          return value.toFixed(1);
        }
        return value.toFixed(1).toString();
      }
      return value as string;
    },
  },
  {
    property: 'sightR',
    display: '戴鏡右',
    style: { backgroundColor: '#cacaff' },
    order: 12,
    onRender: (value, i, e) => {
      // if (value === -1) return '<0.1';
      // transformTextInput.bind(null, SightPropertyFields.sightR)
      if (e) return SightPropertyFields.sightR;
      if (typeof value === 'number') {
        if (value === -1) return '<0.1';
        if (value === -5) return '<0.1(全盲)';
        return value % 1 === 0 ? value.toFixed(1) : value.toString();
      }
    },
  },
  {
    property: 'sightL',
    display: '戴鏡左',
    order: 13,
    style: { backgroundColor: '#cacaff' },
    onRender: (value, i, e) => {
      // if (value === -1) return '<0.1';
      // transformTextInput.bind(null, SightPropertyFields.sightL)
      if (e) return SightPropertyFields.sightL;
      if (typeof value === 'number') {
        if (value === -1) return '<0.1';
        if (value === -5) return '<0.1(全盲)';
        return value % 1 === 0 ? value.toFixed(1) : value.toString();
      }
    },
  },
  {
    property: 'isDilating',
    display: '散瞳',
    onRender: transformCheckedBox.bind(null, 'isDilating'),
    order: 14,
  },
  {
    property: 'eNearR',
    display: '近視右',
    order: 16,
    onRender: transformTextInput.bind(null, SightPropertyFields.eNearR),
  },
  {
    property: 'eNearL',
    display: '近視左',
    order: 17,
    onRender: transformTextInput.bind(null, SightPropertyFields.eNearL),
  },
  {
    property: 'eFarR',
    display: '遠視右',
    order: 18,
    onRender: transformTextInput.bind(null, SightPropertyFields.eFarR),
  },
  {
    property: 'eFarL',
    display: '遠視左',
    order: 19,
    onRender: transformTextInput.bind(null, SightPropertyFields.eFarL),
  },
  {
    property: 'eSanR',
    display: '散光右',
    order: 20,
    onRender: transformTextInput.bind(null, SightPropertyFields.eSanR),
  },
  {
    property: 'eSanL',
    display: '散光左',
    order: 21,
    onRender: transformTextInput.bind(null, SightPropertyFields.eSanL),
  },
  {
    property: 'eNear',
    display: '近視',
    onRender: transformCheckedBox.bind(null, 'eNear'),
    order: 22,
  },
  {
    property: 'eFar',
    display: '遠視',
    onRender: transformCheckedBox.bind(null, 'eFar'),
    order: 23,
  },
  {
    property: 'eSan',
    display: '散光',
    onRender: transformCheckedBox.bind(null, 'eSan'),
    order: 24,
  },
  {
    property: 'eWeak',
    display: '弱視',
    onRender: transformCheckedBox.bind(null, 'eWeak'),
    order: 25,
  },
  {
    property: 'noProblem',
    display: '複檢無異狀',
    onRender: transformCheckedBox.bind(null, 'noProblem'),
    order: 26,
  },
  {
    property: 'eSight99',
    display: '其他',
    onRender: transformCheckedBox.bind(null, 'eSight99'),
    order: 27,
  },
  {
    property: 'eSight99State',
    display: '其他陳述',
    style: { width: '9%', whiteSpace: 'nowrap' },
    onRender: (v, i, e) =>
      e ? (
        <TextField property="eSight99State" />
      ) : (
        <OverflowWithTooltip>{v}</OverflowWithTooltip>
      ),
    order: 28,
  },
  {
    property: 'manageID',
    display: '處置代號',
    order: 29,
    onRender: function renderManageId(v, i, e, c) {
      if (
        v === undefined ||
        v === null ||
        (Array.isArray(v) && v.length > 0 && typeof v[0] !== 'string')
      ) {
        v = [];
      }
      const sortedManageIds = Array.isArray(v)
        ? [...v].sort((a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
        : [];
      const { [SIGHT_MANAGE_MAP]: SightMap = I18N.SightManageCodeMap } =
        (c.context as typeof SightContext) || SightContext;
      return e ? (
        SightPropertyFields.manageID(SightMap)
      ) : (
        <OverlayTrigger
          overlay={
            <Tooltip id={`sight-manage-${i}`}>
              {v ? (
                <table>
                  <tbody>
                    {/* {(v as string[]).map((v, idx) => (
                      <tr key={`sight-manage-${idx}`}>
                        <td>{v}</td>
                        <td>:</td>
                        <td>{SightMap[v] || '-1'}</td>
                      </tr>
                    ))} */}
                    {sortedManageIds.map((v, idx) => (
                      <tr key={`sight-manage-${idx}`}>
                        <td>{v}</td>
                        <td>:</td>
                        <td>{SightMap[v] || '-1'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <>無資料</>
              )}
            </Tooltip>
          }
        >
          <span className="btn btn-outline-secondary border-0 px-2 py-1 m-0 mr-1">
            <span className="feather icon-search" />
          </span>
        </OverlayTrigger>
      );
    },
  },
  {
    property: 'manage',
    display: '醫師建議處置',
    style: { width: '10%', whiteSpace: 'nowrap' },
    onRender: (v, i, e) =>
      e ? (
        <TextField property="manage" />
      ) : (
        <OverflowWithTooltip>{v}</OverflowWithTooltip>
      ),
    order: 28,
  },
  {
    property: 'periodical',
    display: '定期檢查',
    order: 30,
    style: { width: '6%' },
    onRender: function datetimeRender(value, idx, editing) {
      if (editing) return <DateTimeField property="periodical" />;
      if (value instanceof DateTime) return value.toFormat('yyyy/MM/dd');
      if (typeof value === 'string') return value;
      return '';
    },
  },
  {
    property: 'examination',
    display: '未受檢原因',
    order: 15,
    onRender: (v, i, e, c) => {
      const allValues = c?.allValues as Sight;

      if (e) {
        return <ExaminationReasonField type="sight" />;
      }

      const reason = getExaminationReasonLabel(
        allValues?.examination,
        allValues?.examinationState
      );

      return (
        <OverlayTrigger
          overlay={
            <Tooltip id={`sight-examination-${i}`}>
              {reason || '無資料'}
            </Tooltip>
          }
        >
          <span className="btn btn-outline-secondary border-0 px-2 py-1 m-0 mr-1">
            <span className="feather icon-search" />
          </span>
        </OverlayTrigger>
      );
    },
  },
];

export const SightCalculators = [
  createCalculator<Sight>(
    {
      field: /eNear[RL]/i,
      updates: {
        eNear: (value: boolean, allValues: Sight | undefined) => {
          return !!allValues && (!!allValues.eNearL || !!allValues.eNearR);
        },
      },
    },
    {
      field: /eFar[RL]/i,
      updates: {
        eFar: (value: boolean, allValues: Sight | undefined) => {
          return !!allValues && (!!allValues.eFarL || !!allValues.eFarR);
        },
      },
    },
    {
      field: /eSan[RL]/i,
      updates: {
        eSan: (value: boolean, allValues: Sight | undefined) => {
          return !!allValues && (!!allValues.eSanL || !!allValues.eSanR);
        },
      },
    },
    {
      field: /sight0R/i,
      updates: {
        sight0L: (value: number | null, allValues: Sight | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sight0L : null;
        },
        sightL: (value: number | null, allValues: Sight | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sightL : null;
        },
        sightR: (value: number | null, allValues: Sight | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sightR : null;
        },
        noProblem: (value: boolean, allValues: Sight | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0R === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0R)
          ) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    {
      field: /sight0L/i,
      updates: {
        noProblem: (value: boolean, allValues: Sight | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0L === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0L)
          ) {
            return false;
          }
          if (!!allValues && allValues.eNear) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    {
      field: /(eSan|eFar|eNear|eWeak|eSight99)/,
      updates: {
        noProblem: (value: boolean, allValues: Sight | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0L === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0L)
          ) {
            return false;
          }
          if (!!allValues && allValues.eNear) {
            return false;
          }
          if (!!allValues && allValues.eFar) {
            return false;
          }
          if (!!allValues && allValues.eSan) {
            return false;
          }
          if (!!allValues && allValues.eWeak) {
            return false;
          }
          if (!!allValues && allValues.eSight99) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    // {
    //   field: /sight0[RL]/i,
    //   updates: {
    //     sight0L: (value: boolean, allValues: Sight | undefined) => {
    //       return (
    //         !!allValues &&
    //         (allValues.sight0R == -9 ? undefined : allValues.sight0L)
    //       );
    //     },
    //     sightL: (value: boolean, allValues: Sight | undefined) => {
    //       return (
    //         !!allValues &&
    //         (allValues.sight0R == -9 ? undefined : allValues.sightL)
    //       );
    //     },
    //     sightR: (value: boolean, allValues: Sight | undefined) => {
    //       return (
    //         !!allValues &&
    //         (allValues.sight0R == -9 ? undefined : allValues.sightR)
    //       );
    //     },
    //   },
    // },
    {
      field: 'eSight99State',
      updates: {
        eSight99: (value: boolean, allValues: Sight | undefined) => {
          return (
            allValues?.eSight99 || !!allValues?.eSight99State?.length || false
          );
        },
      },
    },
    {
      field: 'manageID',
      updates: {
        manage: (value: boolean, allValues: Sight | undefined) => {
          if (
            Array.isArray(allValues?.manageID) &&
            allValues?.manageID?.length
          ) {
            const ordermanageID = allValues?.manageID
              .map((id) => (id));
            // .sort((a, b) => a - b);
            return manageIDMap[ordermanageID[0]];
          }
          return allValues?.manage;
        },
      },
    }
  ),
];
export const ClassSightCalculators = [
  createCalculator<EyeSightPageData>(
    {
      field: /eNear[RL]/i,
      updates: {
        eNear: (value: boolean, allValues: EyeSightPageData | undefined) => {
          return !!allValues && (!!allValues.eNearL || !!allValues.eNearR);
        },
      },
    },
    {
      field: /eFar[RL]/i,
      updates: {
        eFar: (value: boolean, allValues: EyeSightPageData | undefined) => {
          return !!allValues && (!!allValues.eFarL || !!allValues.eFarR);
        },
      },
    },
    {
      field: /eSan[RL]/i,
      updates: {
        eSan: (value: boolean, allValues: EyeSightPageData | undefined) => {
          return !!allValues && (!!allValues.eSanL || !!allValues.eSanR);
        },
      },
    },
    {
      field: /sight0R/i,
      updates: {
        sight0L: (value: number | null, allValues: EyeSightPageData | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sight0L : null;
        },
        sightL: (value: number | null, allValues: EyeSightPageData | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sightL : null;
        },
        sightR: (value: number | null, allValues: EyeSightPageData | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sightR : null;
        },
        noProblem: (value: boolean, allValues: EyeSightPageData | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0R === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0R)
          ) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
        examination: (
          value: number | null,
          allValues: EyeSightPageData | undefined
        ) => {
          if (Number(allValues?.sight0R) === -9) {
            return allValues?.examination ?? null;
          }

          return null;
        },

        examinationState: (
          value: string | null,
          allValues: EyeSightPageData | undefined
        ) => {
          if (Number(allValues?.sight0R) === -9) {
            return allValues?.examinationState ?? null;
          }

          return null;
        },
      },
    },
    {
      field: /sight0L/i,
      updates: {
        noProblem: (value: boolean, allValues: EyeSightPageData | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0L === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0L)
          ) {
            return false;
          }
          if (!!allValues && allValues.eNear) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
        sightL: (value: number | null, allValues: EyeSightPageData | undefined) => {
          if (allValues?.sight0L == -9) {
            return null;
          }
          return allValues ? allValues.sightL : null;
        },
        examination: (
          value: number | null,
          allValues: EyeSightPageData | undefined
        ) => {
          if (Number(allValues?.sight0L) === -9) {
            return allValues?.examination ?? null;
          }

          return null;
        },
        examinationState: (
          value: string | null,
          allValues: EyeSightPageData | undefined
        ) => {
          if (Number(allValues?.sight0L) === -9) {
            return allValues?.examinationState ?? null;
          }

          return null;
        },
      },
    },
    {
      field: /(eSan|eFar|eNear|eWeak|eSight99)/,
      updates: {
        noProblem: (value: boolean, allValues: EyeSightPageData | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0L === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0L)
          ) {
            return false;
          }
          if (!!allValues && allValues.eNear) {
            return false;
          }
          if (!!allValues && allValues.eFar) {
            return false;
          }
          if (!!allValues && allValues.eSan) {
            return false;
          }
          if (!!allValues && allValues.eWeak) {
            return false;
          }
          if (!!allValues && allValues.eSight99) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    // {
    //   field: /sight0[RL]/i,
    //   updates: {
    //     sight0L: (value: boolean, allValues: EyeSightPageData | undefined) => {
    //       return (
    //         !!allValues &&
    //         (allValues.sight0R == -9 ? undefined : allValues.sight0L)
    //       );
    //     },
    //     sightL: (value: boolean, allValues: EyeSightPageData | undefined) => {
    //       return (
    //         !!allValues &&
    //         (allValues.sight0R == -9 ? undefined : allValues.sightL)
    //       );
    //     },
    //     sightR: (value: boolean, allValues: EyeSightPageData | undefined) => {
    //       return (
    //         !!allValues &&
    //         (allValues.sight0R == -9 ? undefined : allValues.sightR)
    //       );
    //     },
    //   },
    // },
    {
      field: 'eSight99State',
      updates: {
        eSight99: (value: boolean, allValues: EyeSightPageData | undefined) => {
          return (
            allValues?.eSight99 || !!allValues?.eSight99State?.length || false
          );
        },
      },
    },
    // {
    //   field: 'manageID',
    //   updates: {
    //     manage: (value: boolean, allValues: EyeSightPageData | undefined) => {
    //       const manageID = allValues?.manageID;
    //       console.log('manageID', manageID);
    //       if (typeof manageID === 'string' && manageID.trim()) {
    //         const firstManageID = manageID.split(',')[0].trim();

    //         return manageIDMap[firstManageID];
    //       }

    //       return allValues?.manage;
    //     },
    //   },
    // },
  ),
];
export const PHISightCalculators = [
  createCalculator<PHISight>(
    {
      field: /eNear[RL]/i,
      updates: {
        eNear: (value: boolean, allValues: Sight | undefined) => {
          console.log(value);
          return !!allValues && (!!allValues.eNearL || !!allValues.eNearR);
        },
      },
    },
    {
      field: /eFar[RL]/i,
      updates: {
        eFar: (value: boolean, allValues: Sight | undefined) => {
          return !!allValues && (!!allValues.eFarL || !!allValues.eFarR);
        },
      },
    },
    {
      field: /eSan[RL]/i,
      updates: {
        eSan: (value: boolean, allValues: Sight | undefined) => {
          return !!allValues && (!!allValues.eSanL || !!allValues.eSanR);
        },
      },
    },
    {
      field: /sight0R/i,
      updates: {
        sight0L: (value: number | null, allValues: Sight | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sight0L : null;
        },
        sightL: (value: number | null, allValues: Sight | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sightL : null;
        },
        sightR: (value: number | null, allValues: Sight | undefined) => {
          if (allValues?.sight0R == -9) {
            return null;
          }
          return allValues ? allValues.sightR : null;
        },
        noProblem: (value: boolean, allValues: Sight | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0R === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0R)
          ) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    {
      field: /sight0L/i,
      updates: {
        noProblem: (value: boolean, allValues: Sight | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0L === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0L)
          ) {
            return false;
          }
          if (!!allValues && allValues.eNear) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    {
      field: /(eSan|eFar|eNear|eWeak|eSight99)/,
      updates: {
        noProblem: (value: boolean, allValues: Sight | undefined) => {
          if (
            !!allValues &&
            typeof allValues.sight0L === 'string' &&
            ['-8', '-7', '-6'].includes(allValues?.sight0L)
          ) {
            return false;
          }
          if (!!allValues && allValues.eNear) {
            return false;
          }
          if (!!allValues && allValues.eFar) {
            return false;
          }
          if (!!allValues && allValues.eSan) {
            return false;
          }
          if (!!allValues && allValues.eWeak) {
            return false;
          }
          if (!!allValues && allValues.eSight99) {
            return false;
          }
          return (!!allValues && allValues.noProblem) || false;
        },
      },
    },
    {
      field: 'eSight99State',
      updates: {
        eSight99: (value: boolean, allValues: Sight | undefined) => {
          return (
            allValues?.eSight99 || !!allValues?.eSight99State?.length || false
          );
        },
      },
    },
    // {
    //   field: 'manageID',
    //   updates: {
    //     manage: (value: boolean, allValues: Sight | undefined) => {
    //       if (
    //         Array.isArray(allValues?.manageID) &&
    //         allValues?.manageID?.length
    //       ) {
    //         const ordermanageID = allValues?.manageID
    //           .map((id) => (id));
    //           // .sort((a, b) => a - b);
    //         return manageIDMap[ordermanageID[0]];
    //       }
    //       return allValues?.manage;
    //     },
    //   },
    // }
  ),
];

// denormalize value to let user easier to key data
// 0.8 => 8
export function denormalizeStudentSight({
  sight0L,
  sight0R,
  sightL,
  sightR,
  ...data
}: Nullable<Sight>): Sight {
  console.log(data)
  return {
    ...data,
    eNear: data.eNear ?? false,
    eFar: data.eFar ?? false,
    eSan: data.eSan ?? false,
    eWeak: data.eWeak ?? false,
    eSight99: data.eSight99 ?? false,
    noProblem: data.noProblem ?? false,
    isDilated: data.isDilated ?? false,
    isDilating: data.isDilating ?? false,
    sight0L: sight0L && sight0L > 0 ? +(sight0L * 10).toFixed(1) : sight0L,
    sight0R: sight0R && sight0R > 0 ? +(sight0R * 10).toFixed(1) : sight0R,
    sightL: sightL && sightL > 0 ? +(sightL * 10).toFixed(1) : sightL,
    sightR: sightR && sightR > 0 ? +(sightR * 10).toFixed(1) : sightR,
  };
}

// normalize value to store right data
// 8 => 0.8
export function normalizeStudentSight({
  sight0L,
  sight0R,
  sightL,
  sightR,
  eSanL,
  eSanR,
  eNear,
  eFar,
  eSan,
  eWeak,
  eSight99,
  noProblem,
  isDilated,
  isDilating,
}: {
  eNear?: boolean | null | undefined;
  eFar?: boolean | null | undefined;
  eSan?: boolean | null | undefined;
  eWeak?: boolean | null | undefined;
  eSight99?: boolean | null | undefined;
  noProblem?: boolean | null | undefined;
  isDilated?: boolean | null | undefined;
  isDilating?: boolean | null | undefined;
  sight0L?: number | null | undefined;
  sight0R?: number | null | undefined;
  sightL?: number | null | undefined;
  sightR?: number | null | undefined;
  eSanL?: number | null | undefined;
  eSanR?: number | null | undefined;
}) {
  return {
    eNear: eNear ?? false,
    eFar: eFar ?? false,
    eSan: eSan ?? false,
    eWeak: eWeak ?? false,
    eSight99: eSight99 ?? false,
    noProblem: noProblem ?? false,
    isDilated: isDilated ?? false,
    isDilating: isDilating ?? false,
    eSanL: eSanL && eSanL > 0 ? -eSanL : eSanL,
    eSanR: eSanR && eSanR > 0 ? -eSanR : eSanR,
    sight0L: sight0L && sight0L > 0 ? +(sight0L / 10).toFixed(1) : sight0L,
    sight0R: sight0R && sight0R > 0 ? +(sight0R / 10).toFixed(1) : sight0R,
    sightL: sightL && sightL > 0 ? +(sightL / 10).toFixed(1) : sightL,
    sightR: sightR && sightR > 0 ? +(sightR / 10).toFixed(1) : sightR,
  };
}
